import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, ChevronRight, CreditCard } from 'lucide-react';
import { LineReveal } from './motion';
import { SITE } from '../data/site';

export default function PageHero({ eyebrow, h1Lines, sub, image, imageAlt, crumbs = [], applyUrl }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);

  return (
    <section ref={ref} className="relative overflow-hidden bg-navy grain" data-testid="page-hero">
      {image && (
        <motion.div className="absolute inset-0" style={{ y }} aria-hidden="true">
          <img src={image} alt={imageAlt} className="h-[115%] w-full object-cover opacity-40" />
        </motion.div>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy/80 to-navy/40" aria-hidden="true" />
      <div className="absolute -right-20 top-0 h-full w-80 -skew-x-12 bg-brand/15" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 font-mono text-xs uppercase tracking-widest text-slate-400">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              {crumbs.map((c) => (
                <li key={c.name} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  {c.path ? <Link to={c.path} className="hover:text-white">{c.name}</Link> : <span className="text-sky-300">{c.name}</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-sky-300" data-testid="page-hero-eyebrow">{eyebrow}</p>
        <LineReveal
          lines={h1Lines}
          className="max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        />
        {sub && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="page-hero-sub"
          >
            {sub}
          </motion.p>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <a
            href={SITE.phoneHref}
            data-testid="page-hero-call-button"
            className="flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-display text-base font-bold text-white shadow-lg shadow-brand/40 transition-all hover:-translate-y-0.5 hover:bg-brand-dark"
          >
            <Phone className="h-5 w-5" /> Call {SITE.phone}
          </a>
          <Link
            to="/contact?form=install"
            data-testid="page-hero-estimate-button"
            className="flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/10"
          >
            Request an Estimate
          </Link>
          {applyUrl && (
            <a
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="page-hero-apply-button"
              className="flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-base font-bold text-brand shadow-lg transition-all hover:-translate-y-0.5 hover:bg-sky-100"
            >
              <CreditCard className="h-5 w-5" /> Apply for Financing Online
            </a>
          )}
        </motion.div>
      </div>
    </section>
  );
}
